import { Component, ElementRef, ViewChild, Input, forwardRef, AfterViewInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Editor di testo minimale basato su contenteditable + document.execCommand.
 * Il value (ngModel / formControl) e' l'HTML contenuto nell'area editabile.
 *
 * Uso:
 *   <app-simple-editor placeholder="Testo" [(ngModel)]="params.email"></app-simple-editor>
 */
@Component({
  selector: 'app-simple-editor',
  templateUrl: 'simple-editor.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SimpleEditorComponent), multi: true }
  ]
})
export class SimpleEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {

  @Input() placeholder: string = '';
  @Input() minHeight: number = 150;

  // larghezza massima (px) delle immagini inserite: oltre viene ridimensionata
  @Input() imageMaxWidth: number = 600;

  @ViewChild('editable') editable: ElementRef;
  @ViewChild('fileInput') fileInput: ElementRef;

  disabled: boolean = false;
  // stato attivo dei pulsanti (bold, italic, ...) aggiornato sulla selezione corrente
  state: any = {};
  // true = modalita' "mostra codice HTML" (textarea con il sorgente)
  showSource: boolean = false;
  sourceHtml: string = '';

  // font web-safe: i value sono esattamente quelli attesi da execCommand('fontName')
  fonts: string[] = ['Arial', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana'];
  // dimensioni in px (applicate come <span style="font-size:..">, vedi setFontSize)
  sizes: any[] = [
    { label: 'Piccolo', value: '12px' },
    { label: 'Normale', value: '14px' },
    { label: 'Grande', value: '18px' },
    { label: 'Molto grande', value: '24px' },
    { label: 'Enorme', value: '32px' }
  ];

  private value: string = '';
  private savedRange: Range = null;
  private onChange: (v: any) => void = () => { };
  private onTouched: () => void = () => { };
  private selectionListener: any;

  constructor() { }

  ngAfterViewInit() {
    // uniforma il separatore di paragrafo (<p> invece di <div>) tra WebView Android/iOS
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch (e) { }

    this.render(this.value);

    this.selectionListener = () => {
      if (this.isFocused()) {
        this.saveSelection();
        this.updateState();
      }
    };
    document.addEventListener('selectionchange', this.selectionListener);
  }

  ngOnDestroy() {
    if (this.selectionListener)
      document.removeEventListener('selectionchange', this.selectionListener);
  }

  // ---- ControlValueAccessor ----

  writeValue(v: any) {
    this.value = v ? String(v) : '';
    this.sourceHtml = this.value;
    if (this.editable)
      this.render(this.value);
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  // ---- eventi area editabile ----

  onInput() {
    var el = this.editable.nativeElement as HTMLElement;
    // se l'utente cancella tutto restano <p><br></p> o <br>: svuoto davvero
    // cosi' torna il placeholder (:empty) e il value e' stringa vuota
    if (this.isEmpty(el))
      el.innerHTML = '';

    this.value = el.innerHTML;
    this.onChange(this.value);
    this.updateState();
  }

  onBlur() {
    this.onTouched();
  }

  // ---- modalita' codice HTML ----

  toggleSource(evt?: Event) {
    if (evt) evt.preventDefault();
    if (this.disabled) return;

    // tolgo il focus ORA, nel click handler: se lo perdesse dopo (quando Angular
    // nasconde il div / distrugge la textarea) il blur -> onTouched scatterebbe
    // durante il change detection -> ExpressionChangedAfterItHasBeenCheckedError
    this.blurActiveElement();

    if (!this.showSource) {
      // visuale -> sorgente: prendo l'HTML corrente dell'area editabile
      this.value = this.isEmpty(this.editable.nativeElement) ? '' : this.editable.nativeElement.innerHTML;
      this.sourceHtml = this.value;
      this.showSource = true;
    } else {
      // sorgente -> visuale: riporto l'HTML nell'area editabile
      this.value = this.sourceHtml || '';
      this.showSource = false;
      this.render(this.value);
      if (this.isEmpty(this.editable.nativeElement)) {
        this.editable.nativeElement.innerHTML = '';
        this.value = '';
      }
      this.savedRange = null;
      this.onChange(this.value);
      this.updateState();
    }
  }

  onSourceInput(evt: any) {
    this.sourceHtml = evt.target.value;
    this.value = this.sourceHtml;
    this.onChange(this.value);
  }

  onKeyup() {
    this.saveSelection();
    this.updateState();
  }

  // incolla sempre come testo semplice (evita HTML sporco da Word/Gmail);
  // se negli appunti c'e' un'immagine la inserisco come immagine
  onPaste(evt: any) {
    var image = this.firstImageFile(evt.clipboardData);
    if (image) {
      evt.preventDefault();
      this.saveSelection();
      this.insertImageFile(image);
      return;
    }

    var text = '';
    if (evt.clipboardData && evt.clipboardData.getData)
      text = evt.clipboardData.getData('text/plain');
    else if ((<any>window).clipboardData)
      text = (<any>window).clipboardData.getData('Text');
    if (text !== undefined && text !== null) {
      evt.preventDefault();
      document.execCommand('insertText', false, text);
      this.onInput();
    }
  }

  onDragOver(evt: any) {
    if (this.disabled) return;
    if (evt.dataTransfer && this.hasFiles(evt.dataTransfer))
      evt.preventDefault();
  }

  // immagine trascinata nell'area: la inserisco nel punto di rilascio
  onDrop(evt: any) {
    if (this.disabled) return;
    var image = this.firstImageFile(evt.dataTransfer);
    if (!image) return;
    evt.preventDefault();

    var range: Range = null;
    var doc: any = document;
    try {
      if (doc.caretRangeFromPoint) {
        range = doc.caretRangeFromPoint(evt.clientX, evt.clientY);
      } else if (doc.caretPositionFromPoint) {
        var pos = doc.caretPositionFromPoint(evt.clientX, evt.clientY);
        if (pos) {
          range = document.createRange();
          range.setStart(pos.offsetNode, pos.offset);
          range.collapse(true);
        }
      }
    } catch (e) { }
    if (range && this.editable.nativeElement.contains(range.commonAncestorContainer))
      this.savedRange = range;

    this.insertImageFile(image);
  }

  // ---- immagini ----
  // l'immagine non viene caricata sul server: viene letta in locale e
  // incorporata nell'HTML come data URI (base64)

  pickImage(evt?: Event) {
    if (evt) evt.preventDefault();
    if (this.disabled || this.showSource) return;
    this.fileInput.nativeElement.click();
  }

  onImageSelected(evt: any) {
    var input = evt.target as HTMLInputElement;
    var file = input.files && input.files.length ? input.files[0] : null;
    // reset: permette di riselezionare lo stesso file
    input.value = '';
    if (file && /^image\//.test(file.type))
      this.insertImageFile(file);
  }

  private insertImageFile(file: File) {
    var reader = new FileReader();
    reader.onload = () => {
      this.resizeImage(reader.result as string, file.type).then((src) => {
        this.insertImageHtml(src);
      });
    };
    reader.readAsDataURL(file);
  }

  // riduce le immagini piu' larghe di imageMaxWidth (le GIF restano
  // invariate per non perdere l'animazione)
  private resizeImage(dataUrl: string, type: string): Promise<string> {
    return new Promise((resolve) => {
      if (type === 'image/gif' || type === 'image/svg+xml') {
        resolve(dataUrl);
        return;
      }
      var img = new Image();
      img.onload = () => {
        if (img.width <= this.imageMaxWidth) {
          resolve(dataUrl);
          return;
        }
        try {
          var w = this.imageMaxWidth;
          var h = Math.round(img.height * w / img.width);
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          // PNG resta PNG (trasparenza), il resto diventa JPEG compresso
          resolve(type === 'image/png' ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.85));
        } catch (e) {
          resolve(dataUrl);
        }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });
  }

  private insertImageHtml(src: string) {
    if (this.disabled || this.showSource) return;

    var el = this.editable.nativeElement as HTMLElement;
    el.focus();
    // nessuna posizione salvata: inserisco in fondo al testo
    if (!this.savedRange) {
      var range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      this.savedRange = range;
    }
    this.restoreSelection();

    var html = '<img src="' + src + '" style="max-width:100%;height:auto;">';
    var ok = false;
    try {
      ok = document.execCommand('insertHTML', false, html);
    } catch (e) { }
    if (!ok) {
      try {
        document.execCommand('insertImage', false, src);
      } catch (e) { }
    }
    this.saveSelection();
    this.onInput();
  }

  private firstImageFile(data: DataTransfer): File {
    if (!data) return null;
    var i: number;
    if (data.files && data.files.length) {
      for (i = 0; i < data.files.length; i++) {
        if (/^image\//.test(data.files[i].type))
          return data.files[i];
      }
    }
    if (data.items) {
      for (i = 0; i < data.items.length; i++) {
        var item = data.items[i];
        if (item.kind === 'file' && /^image\//.test(item.type))
          return item.getAsFile();
      }
    }
    return null;
  }

  private hasFiles(data: DataTransfer): boolean {
    var types: any = data.types || [];
    for (var i = 0; i < types.length; i++) {
      if (types[i] === 'Files') return true;
    }
    return false;
  }

  // ---- toolbar ----

  // preventDefault sul mousedown: il click sul bottone non deve togliere
  // il focus (e quindi la selezione) all'area editabile
  onToolbarMousedown(evt: Event) {
    // le <select> hanno bisogno del focus per aprirsi: per loro non blocco il
    // mousedown; la selezione viene comunque ripristinata da focusEditable()
    var target = evt.target as HTMLElement;
    if (target && target.tagName === 'SELECT') return;
    evt.preventDefault();
  }

  exec(command: string, evt?: Event) {
    if (evt) evt.preventDefault();
    if (this.disabled || this.showSource) return;

    this.focusEditable();
    try {
      document.execCommand(command, false, null);
    } catch (e) { }
    this.onInput();
  }

  clearFormat(evt?: Event) {
    if (evt) evt.preventDefault();
    if (this.disabled || this.showSource) return;

    this.focusEditable();
    try {
      document.execCommand('removeFormat', false, null);
      // togli eventuali liste attive
      if (document.queryCommandState('insertUnorderedList'))
        document.execCommand('insertUnorderedList', false, null);
      if (document.queryCommandState('insertOrderedList'))
        document.execCommand('insertOrderedList', false, null);
      document.execCommand('formatBlock', false, 'p');
    } catch (e) { }
    this.onInput();
  }

  // font: execCommand('fontName') produce <font face="..."> (o span con
  // font-family, a seconda del browser)
  setFontName(evt: any) {
    var fontFamily = evt.target.value;
    if (this.disabled || this.showSource || !fontFamily) return;

    this.focusEditable();
    try {
      document.execCommand('fontName', false, fontFamily);
    } catch (e) { }
    this.onInput();
  }

  // dimensione in px: execCommand('fontSize') accetta solo 1-7, quindi uso
  // il valore 7 come "marcatore" e subito dopo converto i <font size="7">
  // generati in <span style="font-size: XXpx">
  setFontSize(evt: any) {
    var px = evt.target.value;
    if (this.disabled || this.showSource || !px) return;

    this.focusEditable();
    try {
      document.execCommand('fontSize', false, '7');
    } catch (e) { }

    var root = this.editable.nativeElement as HTMLElement;
    var fonts = root.querySelectorAll('font[size="7"]');
    var first: HTMLElement = null;
    var last: HTMLElement = null;
    for (var i = 0; i < fonts.length; i++) {
      var font = fonts[i] as HTMLElement;
      var span = document.createElement('span');
      span.style.fontSize = px;
      // non perdere face/color se il browser li ha messi sullo stesso <font>
      var face = font.getAttribute('face');
      var color = font.getAttribute('color');
      if (face) span.style.fontFamily = face;
      if (color) span.style.color = color;
      while (font.firstChild)
        span.appendChild(font.firstChild);
      font.parentNode.replaceChild(span, font);
      if (!first) first = span;
      last = span;
    }

    // dopo la sostituzione dei nodi la selezione va ricreata sugli span nuovi
    if (first) {
      try {
        var range = document.createRange();
        range.setStart(first, 0);
        range.setEnd(last, last.childNodes.length);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        this.savedRange = range.cloneRange();
      } catch (e) { }
    }

    this.onInput();
  }

  // ---- helpers ----

  private blurActiveElement() {
    var active = document.activeElement as HTMLElement;
    if (!active || !active.blur) return;
    var host = this.editable.nativeElement.parentElement as HTMLElement;
    if (host && host.contains(active) && active !== host)
      active.blur();
  }

  private render(html: string) {
    var el = this.editable.nativeElement as HTMLElement;
    if (el.innerHTML !== html)
      el.innerHTML = html;
  }

  private isEmpty(el: HTMLElement): boolean {
    var text = (el.textContent || '').replace(/ /g, ' ').trim();
    if (text.length > 0) return false;
    // contenuti senza testo ma significativi
    return el.querySelector('img, li, hr, table') == null;
  }

  private isFocused(): boolean {
    return this.editable && document.activeElement === this.editable.nativeElement;
  }

  private focusEditable() {
    var el = this.editable.nativeElement as HTMLElement;
    if (document.activeElement !== el)
      el.focus();
    this.restoreSelection();
  }

  private saveSelection() {
    var sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      var range = sel.getRangeAt(0);
      if (this.editable.nativeElement.contains(range.commonAncestorContainer))
        this.savedRange = range.cloneRange();
    }
  }

  private restoreSelection() {
    if (!this.savedRange) return;
    var sel = window.getSelection();
    if (!sel) return;
    try {
      sel.removeAllRanges();
      sel.addRange(this.savedRange);
    } catch (e) { }
  }

  private updateState() {
    var cmds = ['bold', 'italic', 'underline', 'insertUnorderedList', 'insertOrderedList',
      'justifyLeft', 'justifyCenter', 'justifyRight'];
    var s: any = {};
    for (var i = 0; i < cmds.length; i++) {
      try {
        s[cmds[i]] = document.queryCommandState(cmds[i]);
      } catch (e) {
        s[cmds[i]] = false;
      }
    }
    s.fontName = this.currentFontName();
    s.fontSize = this.currentFontSize();
    this.state = s;
  }

  // font della selezione corrente, normalizzato a uno dei value della select
  // (queryCommandValue puo' restituire '"Times New Roman"' con virgolette,
  // oppure una lista 'Arial, sans-serif')
  private currentFontName(): string {
    var raw = '';
    try {
      raw = document.queryCommandValue('fontName') || '';
    } catch (e) { }
    var name = raw.split(',')[0].replace(/["']/g, '').trim().toLowerCase();
    for (var i = 0; i < this.fonts.length; i++) {
      if (this.fonts[i].toLowerCase() === name)
        return this.fonts[i];
    }
    return '';
  }

  // dimensione della selezione corrente: cerco risalendo dal nodo del cursore
  // il primo elemento con font-size inline (quelli creati da setFontSize)
  private currentFontSize(): string {
    var sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return '';
    var node: Node = sel.getRangeAt(0).startContainer;
    var root = this.editable.nativeElement as HTMLElement;
    while (node && node !== root) {
      if (node.nodeType === 1) {
        var fs = (node as HTMLElement).style.fontSize;
        if (fs) {
          for (var i = 0; i < this.sizes.length; i++) {
            if (this.sizes[i].value === fs)
              return fs;
          }
          return '';
        }
      }
      node = node.parentNode;
    }
    return '';
  }
}



// WEBPACK FOOTER //
// ./src/components/simple-editor/simple-editor.ts